/** @jsxImportSource @emotion/react */
import * as React from 'react';
import * as R from 'ramda';
import { ApiError } from '../../core/redux/api/apiError';
import { connect } from 'react-redux';
import { ResortToolsConfig } from '../../auth/config/resortToolsConfig';
import { Column } from '../../layout/flexbox';
import { assoc } from '../../language/module';
import { CONFIG_KEY } from '../../auth/config/configKey';
import { IAppState } from '../../core/redux/IAppState';
import { getError, getLoading } from '../../core/redux/api/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { css } from '@emotion/react';
import { saveConfigAction } from './saveConfigAction';
import { SYSTEM_ADMIN_SECTION } from './SystemAdminSection';
import { showSnackbarAction } from '../../snackbar/snackbarAction';
import BlockingLoader from '../../forms/elements/loader/BlockingLoader';
import { PAGE_COLUMN, PAGE_CONTENT, PAGE_TITLE } from '../../styles/styles';
import AutoForm from '../../forms/autoForm/AutoForm';
import { InputType } from '../../forms/autoForm/inputType';
import CollapsibleGroup from '../../layout/CollapsibleGroup';
import { fromFormModel, toFormModel } from './module';
import ConfigRedirect from '../../auth/ConfigRedirect';
import { ROOT_PATH } from '../../core/routes';
import {resortIdToAbbreviation, resortIdToResortName} from './systemAdminHelper';
import { clearApiErrorAction } from '../../core/redux/api/clearApiErrorAction';
import { DEFAULT_CONFIG } from '../../auth/config/defaultConfig';
import { Loader } from '../../forms/elements/loader/Loader';
import { getResource } from '../../language/resources/getResource';
import { VersionResponse } from './versionResponse';
import { formatDate } from '../../language/time';


const NoLabelToggling = css`
  pointer-events: none;
`;

const FeatureToggleMenu = css`
  width: 1000px;
  margin-left: 25px;
  padding-bottom: 25px;
  flex-wrap: wrap;
  align-content: space-between;
  height: 980px;
`;


type Props = {
  // Props
  config?: ResortToolsConfig;
  saveConfig?: (config: ResortToolsConfig) => void;
  showSnackbar?: (message: string) => void;
  showError?: (message:string) => void;
  clearError?: (requestId: string, actionType: string) => void;

  // Loading
  isLoadingConfig? :boolean;
  isSaving?: boolean;
  // Error
  errorConfig?: ApiError;
  loadErrorConfig?: ApiError;

  isGlobalHotlistEnabled?: boolean;
  isResortSpecificHotlistEnabled?: boolean;
  roles: string[];
  resortConfig: ResortToolsConfig;

  resortAbbrev: string;
  resortName: string;
  currentUserId: string;
  currentLocale: Record<string, any>;
  versionHistory: VersionResponse[];
  isRtp: boolean;
  dateFormat: string;
};


type State = {
  lastSubmitted: SYSTEM_ADMIN_SECTION | undefined;
  formConfig: object | undefined;
  isGlobalHotlistEnabled?: boolean;
  isResortSpecificHotlistEnabled?: boolean;
  isAnyHotlistEnabled?: boolean;
};


/**
 * SystemAdminPage: a component that allows users to manage site settings at a system level
 *
 * Retrieves the config from Redux and allows an authorized user to alter it, and to be able to submit those changes
 * to the server to affect all users.
 *
 * NOTE: Currently, system config settings are all or none and not role-based.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState) => ({
    config:                         state.resortToolsConfig,
    isSaving:                       !!getLoading(
      state,
      ACTION_TYPE.UPDATE_CONFIG_REQUEST,
      ACTION_TYPE.UPDATE_CONFIG_REQUEST,
    ),
    isLoadingConfig:                       !!getLoading(
      state,
      ACTION_TYPE.APP_CONFIG_REQUEST,
      ACTION_TYPE.APP_CONFIG_REQUEST,
    ),
    errorConfig:                    getError(
      state,
      ACTION_TYPE.UPDATE_CONFIG_REQUEST,
      ACTION_TYPE.UPDATE_CONFIG_REQUEST,
    ),
    loadErrorConfig:                getError(
      state,
      ACTION_TYPE.APP_CONFIG_REQUEST,
      ACTION_TYPE.APP_CONFIG_REQUEST,
    ),
    isGlobalHotlistEnabled:         state.resortToolsConfig.enableGlobalHotlist,
    isResortSpecificHotlistEnabled: state.resortToolsConfig.enableResortSpecificHotlist,
    roles:                          state.resortToolsConfig.userRoles,
    resortConfig:                   state.resortToolsConfig,
    resortAbbrev: resortIdToAbbreviation(state.selectedResort.resortId, state.resortDetails?.allResorts.getValues() || []),
    resortName: resortIdToResortName(state.selectedResort.resortId, state.resortDetails?.allResorts.getValues() || []),
    currentUserId:                  state.userState.userId,
    currentLocale:                  state.locale,
    versionHistory:                 state.scriptVersions,
    isRtp:                          state.resortMappingHistories.posProvider === 'RTP',
    dateFormat:                     state.defaultDateFormat,
  }),
  (dispatch) => ({
    saveConfig:   (config: ResortToolsConfig) => dispatch(saveConfigAction(config)),
    showSnackbar: (message: string) => dispatch(showSnackbarAction(message)),
    showError: (message: string) => dispatch(showSnackbarAction(message, true, 5000, 1000)),
    clearError: (requestId: string, actionType: string) => dispatch(clearApiErrorAction(requestId, actionType)),
  }),
)(class SystemAdminPage extends React.PureComponent<Props, State> {
  public state: State = {
    lastSubmitted:                  undefined,
    formConfig:                     toFormModel(this.props.config!),
    isGlobalHotlistEnabled:         this.props.isGlobalHotlistEnabled,
    isResortSpecificHotlistEnabled: this.props.isResortSpecificHotlistEnabled,
    isAnyHotlistEnabled:            this.props.isGlobalHotlistEnabled || this.props.isResortSpecificHotlistEnabled,
  };

  public componentDidUpdate(prevProps: Props) {
    // If the config changed, translate the changes into a format the forms can digest
    if (prevProps.config !== this.props.config || prevProps.resortAbbrev !== this.props.resortAbbrev) {
      this.setState(prev => assoc('formConfig', toFormModel(this.props.config!), prev));
    }

    // No longer loading + no error -> saved successfully
    if (prevProps.isSaving && !this.props.isSaving && !this.props.errorConfig) {
      this.setState(prev => assoc('lastSubmitted', undefined, prev));
      this.props.showSnackbar!(getResource(this.props.currentLocale , 'SAVE_CONFIG_SUCCESS_MSG'));
    }

    if (this.props.loadErrorConfig)
    {
      this.props.showError!(getResource(this.props.currentLocale, 'GET_CONFIG_FETCH_ERROR', this.props.resortName));   
      this.props.clearError!(this.props.loadErrorConfig.requestId, this.props.loadErrorConfig.actionType);  
      this.setState(prev => assoc('formConfig', toFormModel(DEFAULT_CONFIG), prev));
    }
  }

  public componentDidMount () {
    if (!this.props.isLoadingConfig) {
      this.setState(prev => assoc('formConfig', toFormModel(this.props.config!), prev));
    }
  };


  private saveConfig = (section: SYSTEM_ADMIN_SECTION, formData) => {
    const config = fromFormModel(formData);
    config.userId = this.props.currentUserId;
    this.setState(prev => assoc('lastSubmitted', section, prev));
    this.props.saveConfig!(config);
  };


  public onFeatureTogglesSubmit = (newValues) => this.saveConfig(SYSTEM_ADMIN_SECTION.FEATURE_TOGGLES, newValues);


  public toggleGlobalHotlistState = (e: Event) => this.setHotlistState(assoc('isGlobalHotlistEnabled',
      !this.state.isGlobalHotlistEnabled, this.state,
    ),
  );

  public toggleResortSpecificHotlistState = (e: Event) => this.setHotlistState(
    assoc('isResortSpecificHotlistEnabled', !this.state.isResortSpecificHotlistEnabled, this.state, ))
  ;

  public setHotlistState = (state: State) => this.setState(
    assoc(
      'isAnyHotlistEnabled',
      state.isGlobalHotlistEnabled || state.isResortSpecificHotlistEnabled,
      state,
    ),
  );


  private getError = (
    key: SYSTEM_ADMIN_SECTION,
  ): string | undefined => key === this.state.lastSubmitted
    ? R.path(['errorConfig', 'errorMsg'], this.props)
    : undefined;



  public render = () => {
    if (this.props.isLoadingConfig) {
      return <Loader rootCss={css`margin-top: auto; margin-bottom: auto;`} />
    }
    return (
    <ConfigRedirect configPath={CONFIG_KEY.MANAGE_SYSTEM_ADMIN} redirectRoute={ROOT_PATH}>
      <Column rootCss={css`${PAGE_COLUMN} width:fit-content; min-width:100%;`}>
        <span css={PAGE_TITLE}>{getResource(this.props.currentLocale, 'SYSTEM_ADMIN_PAGE_TITLE')}</span>
        <div
          css={css`position: relative;
            ${PAGE_CONTENT};`}
        >
          { // Loading
            this.props.isSaving && <BlockingLoader />
          }

          <CollapsibleGroup header={getResource(this.props.currentLocale, 'FEATURE_TOGGLES')}>
            <AutoForm
              resources={this.props.currentLocale}
              error={this.getError(SYSTEM_ADMIN_SECTION.FEATURE_TOGGLES)}
              formDef={[
                {
                  propName:      'enableGlobalHotlist',
                  type:          InputType.TOGGLE,
                  label:         getResource(this.props.currentLocale, 'ALL_RESORTS_HOTLIST_LABEL'),
                  info:          getResource(this.props.currentLocale, 'ALL_RESORTS_HOTLIST_INFO'),
                  css:           NoLabelToggling,
                  changeHandler: this.toggleGlobalHotlistState,
                },
                {
                  propName:      'enableResortSpecificHotlist',
                  type:          InputType.TOGGLE,
                  label:         getResource(this.props.currentLocale, 'RESORT_SPECIFIC_HOTLIST_LABEL'),
                  info:          getResource(this.props.currentLocale, 'RESORT_SPECIFIC_HOTLIST_INFO'),
                  css:           NoLabelToggling,
                  changeHandler: this.toggleResortSpecificHotlistState,
                },
                {
                  propName:  'enableSearchHistory',
                  type:      InputType.TOGGLE,
                  label:     getResource(this.props.currentLocale, 'SEARCH_HISTORY_LABEL'),
                  info:      getResource(this.props.currentLocale, 'SEARCH_HISTORY_INFO'),
                  css:       NoLabelToggling,
                },
                {
                  propName:  'enablePowCoin',
                  type:      InputType.TOGGLE,
                  label:     getResource(this.props.currentLocale, 'POWCOIN_LABEL'),
                  info:      getResource(this.props.currentLocale, 'POWCOIN_INFO'),
                  css:       NoLabelToggling,
                },
                {
                  propName:  'enableChangeHistory',
                  type:      InputType.TOGGLE,
                  label:     getResource(this.props.currentLocale, 'CHANGE_HISTORY_LABEL'),
                  info:      getResource(this.props.currentLocale, 'CHANGE_HISTORY_INFO'),
                  css:       NoLabelToggling,
                },
                {
                  propName:  'enableSyncCustomerMediaUpdate',
                  type:      InputType.TOGGLE,
                  label:     getResource(this.props.currentLocale, 'ENABLE_CUSTOMER_MEDIA_UPDATE_LABEL'),
                  info:      getResource(this.props.currentLocale, 'ENABLE_CUSTOMER_MEDIA_UPDATE_INFO'),
                  css:       NoLabelToggling,
                },
                {
                  propName:  'enableMappingErrorBanner',
                  type:      InputType.TOGGLE,
                  label:     getResource(this.props.currentLocale, 'MAPPING_ERROR_BANNER_LABEL'),
                  info:      getResource(this.props.currentLocale, 'MAPPING_ERROR_BANNER_INFO'),
                  css:       NoLabelToggling,
                },
                {
                  propName:  'enableLanguage',
                  type:      InputType.TOGGLE,
                  label:     getResource(this.props.currentLocale, 'ENABLE_LANGUAGE_LABEL'),
                  info:      getResource(this.props.currentLocale, 'ENABLE_LANGUAGE_INFO'),
                  css:       NoLabelToggling,
                },
                {
                  propName:  'enablePassReprintNotifications',
                  type:      InputType.TOGGLE,
                  label:     getResource(this.props.currentLocale, 'ENABLE_PASS_REPRINT_NOTIFICATIONS_LABEL'),
                  info:      getResource(this.props.currentLocale, 'ENABLE_PASS_REPRINT_NOTIFICATIONS_INFO'),
                  css:       NoLabelToggling,
                },
                {
                  propName:  'displayDeactivatedMediaHistory',
                  type:      InputType.TOGGLE,
                  label:     getResource(this.props.currentLocale, 'DISPLAY_DEACTIVATED_MEDIA_HISTORY_LABEL'),
                  info:      getResource(this.props.currentLocale, 'DISPLAY_DEACTIVATED_MEDIA_HISTORY_INFO'),
                  css:       NoLabelToggling,
                },
                {
                  propName:  'autoLogoutTimerMs',
                  type:      InputType.NUMBER,
                  label:     getResource(this.props.currentLocale, 'AUTO_LOGOUT_TIMER_LABEL'),
                  info:      getResource(this.props.currentLocale, 'AUTO_LOGOUT_TIMER_INFO'),
                  css:       NoLabelToggling,
                  extras: {
                    max: 480,
                    min: 0
                  }
                },
                {
                  propName: 'enableCustomerImport',
                  type:      InputType.MULTI,
                  label:     getResource(this.props.currentLocale, 'ENABLE_CUSTOMER_IMPORT_LABEL'),
                  info:      getResource(this.props.currentLocale, 'ENABLE_CUSTOMER_IMPORT_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role })),
                  }
                },
                {
                  propName: 'enableAddToHotlist',
                  type:     InputType.MULTI,
                  label:     getResource(this.props.currentLocale, 'ADD_TO_HOTLIST_LABEL'),
                  info:      getResource(this.props.currentLocale, 'ADD_TO_HOTLIST_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName: 'enableRemoveFromHotlist',
                  type:     InputType.MULTI,
                  label:     getResource(this.props.currentLocale, 'REMOVE_FROM_HOTLIST_LABEL'),
                  info:      getResource(this.props.currentLocale, 'REMOVE_FROM_HOTLIST_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName: 'enableDtw',
                  type:     InputType.MULTI,
                  label:     getResource(this.props.currentLocale, 'ENABLE_DTW_LABEL'),
                  info:      getResource(this.props.currentLocale, 'ENABLE_DTW_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName: 'enableCorrectiveRedemption',
                  type:     InputType.MULTI,
                  label:     getResource(this.props.currentLocale, 'ENABLE_CORRECTIVE_REDEMPTION_LABEL'),
                  info:      getResource(this.props.currentLocale, 'ENABLE_CORRECTIVE_REDEMPTION_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName: 'manageUsers',
                  type:     InputType.MULTI,
                  label:     getResource(this.props.currentLocale, 'MANAGE_USERS_LABEL'),
                  info:      getResource(this.props.currentLocale, 'MANAGE_USERS_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName: 'manageDays',
                  type:     InputType.MULTI,
                  label:    getResource(this.props.currentLocale, 'MANAGE_DAYS_LABEL'),
                  info:     getResource(this.props.currentLocale, 'MANAGE_DAYS_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName: 'enableHotlistReport',
                  type:     InputType.MULTI,
                  label:    getResource(this.props.currentLocale, 'ENABLE_HOTLIST_REPORT_LABEL'),
                  info:     getResource(this.props.currentLocale, 'ENABLE_HOTLIST_REPORT_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName: 'enableGuestHydrationReport',
                  type:     InputType.MULTI,
                  label:    getResource(this.props.currentLocale, 'ENABLE_GUEST_HYDRATION_REPORT_LABEL'),
                  info:     getResource(this.props.currentLocale, 'ENABLE_GUEST_HYDRATION_REPORT_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName: 'enableWeeklyVisitorReport',
                  type:     InputType.MULTI,
                  label:    getResource(this.props.currentLocale, 'ENABLE_WEEKLY_VISITOR_REPORT_LABEL'),
                  info:     getResource(this.props.currentLocale, 'ENABLE_WEEKLY_VISITOR_REPORT_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName: 'enablePassUsageCorrection',
                  type:     InputType.MULTI,
                  label:    getResource(this.props.currentLocale, 'ENABLE_PASS_USAGE_CORRECTION_LABEL'),
                  info:     getResource(this.props.currentLocale, 'ENABLE_PASS_USAGE_CORRECTION_INFO'),
                  extras: {
                    options: this.props.roles.map((role) => ({ value: role, label: role})),
                  }
                },
                {
                  propName:  'weeklyVisitorReportLimit',
                  type:      InputType.NUMBER,
                  label:     getResource(this.props.currentLocale, 'WEEKLY_VISITOR_REPORT_LIMIT_LABEL'),
                  info:      getResource(this.props.currentLocale, 'WEEKLY_VISITOR_REPORT_LIMIT_INFO'),
                  css:       NoLabelToggling,
                  extras: {
                    max: 4,
                    min: 0
                  }
                },
                /*defaultValue: this.props.roles.map((role) => ({ value: role, label: role })),
                  options={this.props.roles.map((role) => ({ value: role, label: role }))}
                  name={'roles'}
                  onChange={(selected) => setRoles(selected.map((role) => role.value))}*/
              ]}
              values={this.state.formConfig!}
              onSubmit={this.onFeatureTogglesSubmit}
              rootCss={FeatureToggleMenu}
              roles={this.props.roles}
            />
          </CollapsibleGroup>
          
          { this.props.isRtp && (
            <CollapsibleGroup header={getResource(this.props.currentLocale, 'RTP_SCRIPT_VERSION')}>
            <span>
              {this.props.versionHistory.map(x => <span css={css `display: block;`}>{x.version}    {formatDate(x.updateDate, this.props.dateFormat)}</span>)}
            </span>
            </CollapsibleGroup>
          )}
          
        </div>
      </Column>
    </ConfigRedirect>
  )};
});    

